import React, { useState } from 'react';
import { required, FormDataConsumer, List, SaveButton, Datagrid, DateField,TextField, BooleanField, NumberField, Create, Edit, FormTab,
  TabbedForm, Toolbar, BooleanInput, TextInput, EditButton, SelectInput, ReferenceInput, AutocompleteInput, Filter,
  ArrayInput, SimpleFormIterator, NumberInput, ArrayField, number, minValue, Labeled } from 'react-admin';
import { Box, Typography, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Paper } from '@material-ui/core';
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import {ScheduledReportButton} from "./components/schedule_report_button.js";
import TrafficPanel from './components/TrafficPanel';
const { validMaxLineSeparated, validInteger } = require('./components/validation.js');
const utils = require('./components/utils.js');
const { SectionHeader } = require('./components/utils.js');
const { RtbConnectionMapField } = require('./components/RtbConnectionMapField.js');

const validateListSize = (values) => validMaxLineSeparated(values, 50);


const twoColumnGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', width: '50%' };
const fullWidth = { gridColumn: '1 / -1' };

function priceAdustmentChoices() {
  const ret = [];
  for (let i =1; i<100; i++)  {
    ret.push({id: i, name: `${i}% Adjustment`});
  }
  return ret;
}

const CustomDateField = ({ record }) => {
  let date = '' + (record.updated_at || record.date);
  date = date.substring(0, date.indexOf('T'));
  return (
    <span>
      <nobr>{date}</nobr>
    </span>
  );
};

const BidderTitle = ({ record }) => {
  return (
    <span>
      Bidder {record ? `"${record.name}"` : ''}{' '}
      {record && record.active ? 'ACTIVE' : 'INACTIVE'}
    </span>
  );
};
/**
 * Adds filtering functionality
 * @param {} props
 */
const BidderFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
    <NumberInput label="Bidder ID" source="search_id" alwaysOn resettable />
  </Filter>
);
export const BidderList = (props) => (
  <List
    {...props}
    filters={<BidderFilter />}
    perPage={25}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="edit">
      <TextField source="code" label="ID" />
      <TextField source="name" label="Name" />
      <NumberField source="revenue_stats.total_impressions" label="WTD Impressions" />,
      <NumberField source="revenue_stats.total_revenue" label="WTD Revenue" options={{ style: 'currency', currency: 'USD' }} />,
      <NumberField source="revenue_stats.total_bid_requests" label="WTD Bid Requests" />
      <TextField source="bidder_type" label="Bidder Type" />
      <CustomDateField label="Update Date" />
      <BooleanField source="active" label="Active" />
      <EditButton variant="contained" color="primary" />
    </Datagrid>
  </List>
);
// Seeds the change log with a "created" entry; the Bidder ID is auto-generated
// server-side, so no code field is collected on create. Supply Chain is enabled
// by default for new bidders — its tab is edit-only, so the field's defaultValue
// can't apply at create time and must be set here instead.
const transformCreate = (data) => {
  if (data.supplychain_enabled === undefined) data.supplychain_enabled = true;
  if (data.openrtb_version === undefined && data.bidder_type === 'RTB') data.openrtb_version = '2.5';
  utils.applyAdstxtRegex(data);
  utils.applyConnectionHeadersMap(data);
  return utils.prependBidderLog(data, 'created');
};

/**
 * <Edit> loads the record from the API and passes it to <BidderForm> unchanged.
 * The Mongo shape stores rtb_connection_info.connection_headers as a Map, but the
 * form's ArrayInput binds to a derived connection_headers_list. Wrap the record
 * so the derived list is populated before render, and the ArrayInput actually
 * shows what's in Mongo instead of an empty iterator.
 *
 * React-admin 3.x doesn't expose queryOptions.select, so this is how we hook the
 * load-side transform without touching the data provider.
 */
const BidderFormWithMaterializedRecord = ({ record, ...props }) => (
  <BidderForm record={utils.materializeConnectionHeadersList(record)} {...props} />
);

/**
 * Shared bidder form rendered by both BidderEdit (formType="edit") and
 * BidderCreate (formType="create"). The main "Bidder" tab and "Bidder Info"
 * tab show in both modes; all other tabs are gated to edit, so creation
 * captures the essentials and the rest is configured after the first save.
 */
const BidderForm = ({ permissions, ...props }) => {
  const isEdit = props.formType === 'edit';
  // White-label gating: non-NoBid orgs (organization_id !== 1) only run RTB bidders
  // and don't expose GAM serving fee, optional domain approval, or max refresh count.
  // NoBid users (org 1, or legacy users with no org) see everything.
  const orgId = permissions && permissions.organization_id;
  const isNobidOrg = orgId == null || Number(orgId) === 1;
  return (
      <TabbedForm toolbar={<CustomToolbar {...props} permissions={permissions} />} {...props}>
        <FormTab label="Bidder">
          <Box style={twoColumnGrid}>
            <SectionHeader title="General" />
            <BooleanInput source="active" label="Active" />
            {isEdit && (
            <Labeled label="Bidder ID">
              <TextField source="code" style={{ fontSize: '1rem' }} />
            </Labeled>
            )}
            <TextInput source="name" label="Bidder Name" validate={required()} style={{ maxWidth: 256 }} />
            {/* <BooleanInput source="default" label="Default" defaultValue={false} validate={required()} /> */}

            {isNobidOrg ? (
            <SelectInput source="bidder_type" label="Bidder Type" emptyText="- None -" allowEmpty
              choices={[
                { id: 'RTB', name: 'RTB' },
                { id: 'PREDICTOR', name: 'PREDICTOR' },
              ]} style={{ maxWidth: 256 }} />
            ) : (
              // Non-NoBid orgs are always RTB: keep the value without showing the selector.
              <TextInput source="bidder_type" defaultValue="RTB" style={{ display: 'none' }} />
            )}
            {isNobidOrg && (
            <NumberInput source="serving_fee" label="GAM Serving Fee %" helperText="Taken out at reporting time" style={{ maxWidth: 256 }} />
            )}
            <SelectInput source="impression_type" label="Impression Type" emptyText="- None -" defaultValue={'BANNER'}
              choices={[
                { id: 'BANNER', name: 'Banner' },
                { id: 'VIDEO', name: 'Video' },
                { id: 'AUDIO', name: 'Audio' },
                { id: 'NATIVE', name: 'Native' },
              ]} style={{ maxWidth: 256 }} />
            {/* <SelectInput source="native_response_type" label="Native Response Type" emptyText="- None -" defaultValue={'NATIVE'}
              choices={[
                { id: 'BANNER', name: 'BANNER' },
                { id: 'NATIVE', name: 'NATIVE' },
              ]} style={{ maxWidth: 256 }} /> */}

            <SectionHeader title="Ads.txt" />
            <Box style={fullWidth}>
              <TextInput
                source="adstxt_config.patterns"
                label="Ads.txt Entries"
                multiline
                fullWidth
                helperText="One entry per line: domain, account ID, relationship, [certification authority ID]. Relationship is direct, reseller, or both (blank = both); the certification authority ID is optional. Example: appnexus.com, 11429, both, f5ab79cb980f11d1. Leave the account ID blank to match any account on a domain (nobid.io,). Entries are OR'd: a site matches if any one of them is present."
              />
            </Box>
            <Box style={fullWidth} mt={1} mb={1}>
              <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 4 }}>
                Generated search pattern (read-only)
              </Typography>
              <FormDataConsumer>
                {({ formData }) => {
                  const cfg = (formData && formData.adstxt_config) || {};
                  const preview = utils.adstxtPatternsToRegex(cfg.patterns || '') || cfg.regex || '';
                  return (
                    <Paper variant="outlined" style={{ padding: '8px 12px' }}>
                      {preview ? (
                        <Typography component="code" color="textSecondary" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                          {preview}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Add entries above to generate the pattern.
                        </Typography>
                      )}
                    </Paper>
                  );
                }}
              </FormDataConsumer>
            </Box>
            <BooleanInput source="adstxt_optional" label="Ads.txt Optional" />
            <BooleanInput source="appadstxt_optional" label="App-Ads.txt Optional" />

            <SectionHeader title="Approvals" />
            <BooleanInput source="domain_approval_required" label="Domain Approval Required" />
            <BooleanInput source="app_approval_required" label="App Approval Required" />
            {isNobidOrg && (
            <BooleanInput source="domain_approval_optional" label="Domain Approval Optional"
              helperText="This is used when the Bidder does not require domain approval for bidding, but we want to keep track of the domains." />
            )}

            <SectionHeader title="Feature Flags" />
            <BooleanInput source="coppa_enabled" label="COPPA Enabled" defaultValue={true} />
            <BooleanInput source="block_bots" label="Bot Traffic Bidding Disabled" defaultValue={true} />
            <BooleanInput source="a2v_enabled" label="A2V Enabled (Audio bidder bids on video impressions)"
              helperText="When ON, this audio bidder receives video impressions, and the adserver rewrites its mp3 VAST into a cached muxed mp4. Requires Settings → A2V → Master kill switch to also be ON." />

            <SectionHeader title="Pricing" />
            <SelectInput source="price_adjustment_percent" label="Price Adjustment Percent" emptyText="- None -" allowEmpty
              choices={priceAdustmentChoices()}
              helperText="This is the portion of the price we want to reduce in order to keep the revenue discrepancy between the Bidder and the platform even. This value is also applied to the floors going out to the Bidder."
              style={{ maxWidth: 256 }} />
            <NumberInput source="publisher_max_bidprice" label="Max Publisher Bid Price" step={0.01}
              helperText="This is the maximum (ceiling) price above which we reduce the bid price going to the Publisher."
              style={{ maxWidth: 256 }} />
            {isNobidOrg && (
            <NumberInput source="max_refresh_count" label="Max Refresh Count" style={{ maxWidth: 256 }} />
            )}
          </Box>

          <FormDataConsumer>
            {({ formData }) =>
              formData.bidder_type === 'DIRECT' && (
                <NumberInput source="traffic_sample_rate" validate={validInteger} defaultValue={100000} label="Traffic Sample Rate (default 100,000)"
                    helperText="Sampling rate. 0: Sampling disabled, 1: Sample all incoming requests, N>1: sample 1 out of N incoming requests" style={{ maxWidth: 256 }} />
              )
            }
          </FormDataConsumer>

          <FormDataConsumer>
            {({ formData }) =>
              formData.bidder_type === 'RTB' && (
                <Box style={twoColumnGrid}>
                  <SectionHeader title="RTB Settings" />
                  <BooleanInput source="user_id_optional" label="User ID Optional" defaultValue={true}/>
                  <BooleanInput source="page_url_required" label="Page URL Required" />
                  <TextInput source="sync_id" label="Sync ID" style={{ maxWidth: 256 }} />
                  <Box style={fullWidth}>
                    <TextInput source="sync_partner_endpoint" label="Partner Sync Endpoint" fullWidth
                      helperText="The partner's sync URL up to where the NoBid callback is appended (often ends with url=, redir=, or redirect=). Example: https://visitor-nobid.omnitagjs.com/visitor/bsync?uid=...&name=nobid&url=" />
                  </Box>
                  <Box style={fullWidth} mb={1}>
                    <Typography variant="caption" color="textSecondary" component="div">
                      Consent macros - use these instead of literal values, or the partner
                      receives the same hardcoded consent on every impression including EU
                      traffic: <code>{'${GDPR}'}</code> <code>{'${GDPR_CONSENT}'}</code>{' '}
                      <code>{'${US_PRIVACY}'}</code> <code>{'${GPP}'}</code>{' '}
                      <code>{'${GPP_SID}'}</code>. The adserver substitutes the real
                      per-request values when the sync fires. With no consent on the request
                      they expand to gdpr=0, empty consent string, us_privacy=1YN-. Partner
                      user-ID macros ($UID, [SOVRNID], {'{partnerId}'}) are left untouched.
                    </Typography>
                  </Box>
                  <TextInput source="sync_macro" label="Partner User-ID Macro" style={{ maxWidth: 256 }}
                    helperText="What the partner replaces with the user ID, e.g. PARTNER_USER_ID, $UID, [SOVRNID]." />
                  <Box style={fullWidth} mt={1} mb={1}>
                    <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 4 }}>
                      Generated sync URL (read-only)
                    </Typography>
                    <Paper variant="outlined" style={{ padding: '8px 12px' }}>
                      {formData.sync_partner_endpoint ? (
                        <Typography component="code" color="textSecondary" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                          {utils.composeBidderSyncUrl(formData.sync_partner_endpoint, formData.sync_macro, formData.code)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Enter a partner endpoint above to generate the sync URL.
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                  <Box style={fullWidth}>
                    <TextInput source="sync_url" label="Advanced: raw Sync URL" fullWidth
                      helperText="Optional. Used only when no Partner Sync Endpoint is set above; otherwise it is generated from the fields above when you save." />
                  </Box>
                  <BooleanInput source="non_self_compete" label="Non-Self-Compete" style={{paddingTop: '15px'}}/>
                  <BooleanInput source="missing_storeurl_disabled" label="Disable Missing App Store URL" style={{paddingTop: '15px'}}/>
                  {/* <BooleanInput source="use_current_bids_as_floor" label="Use current bids as floor." /> always true now*/}
                  {isNobidOrg && (
                  <BooleanInput source="bid_floor_enabled" label="Send predicted floors to Bidder." />
                  )}
                  <NumberInput source="traffic_sample_rate" validate={validInteger} defaultValue={100000} label="Traffic Sample Rate (default 100,000)"
                    helperText="Sampling rate. 0: Sampling disabled, 1: Sample all incoming requests, N>1: sample 1 out of N incoming requests" style={{ maxWidth: 256 }} />

                  <SectionHeader title="Revshare" />
                  <NumberInput source="technology_revshare" label="Technology Revshare %"
                    helperText="This is the only revshare that is going to be applied. This is usually set when using the Publisher Bidder account."
                    style={{ maxWidth: 256 }} />
                  <BooleanInput source="technology_revshare_cumulative" label="Technology Revshare is Cumulative"
                    helperText="Apply first the Technology revshare, second the Platform Revshare, third the Publisher Revshare." />
                  <NumberInput source="nobid_revshare" label="Platform Revshare %"
                    helperText="Overrides the org Revshare from the Site/Publisher (default 30%). Does not replace the Publisher Revshare, which is still applied after."
                    style={{ maxWidth: 256 }} />

                  <SectionHeader title="Blocked Advertiser Domains" />
                  <Box style={fullWidth}>
                    <TextInput multiline label="Blocked Advertiser Domains" source="badv" validate={validateListSize}
                      helperText="CASE SENSITIVE. Format: advertiserdomain1.com<NEW_LINE>advertiserdomain2.com"
                      style={{ width: '60%' }} />
                  </Box>
                </Box>
              )
            }
          </FormDataConsumer>
        </FormTab>

        <FormDataConsumer>
          {({ formData, ...rest }) =>
            isEdit && formData.bidder_type === 'PREDICTOR' && (
              <FormTab label="Markup Settings" {...rest}>
                <Box fullWidth>
                  <TextInput
                    source="markup"
                    label="Markup"
                    multiline={true}
                    fullWidth
                  />
                </Box>
                <Box fullWidth>
                  <TextInput
                    source="second_markup"
                    label="Secondary Markup"
                    multiline={true}
                    fullWidth
                  />
                </Box>
              </FormTab>
            )
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData, ...rest }) =>
            isEdit && formData.bidder_type === 'RTB' && (
              <FormTab label="RTB Connection" {...rest}>
                <Box style={twoColumnGrid}>
                  <SectionHeader title="Connection" />
                  <BooleanInput source="is_method_get" label="Get Method" defaultValue={false} />
                  <Box style={fullWidth}>
                    <div style={{ marginBottom: '8px' }}>
                      Macros: &#123;&#123;SEAT_ID&#125;&#125;,
                      &#123;&#123;SITE_ID&#125;&#125;,
                      &#123;&#123;SITE_NAME&#125;&#125;,
                      &#123;&#123;PUBLISHER_ID&#125;&#125;,
                      &#123;&#123;PUBLISHER_NAME&#125;&#125;,
                      &#123;&#123;GEO_LONGITUDE&#125;&#125;,
                      &#123;&#123;GEO_LATITUDE&#125;&#125;,
                      &#123;&#123;CACHE_BUSTER&#125;&#125;,
                      &#123;&#123;USER_AGENT&#125;&#125;,
                      &#123;&#123;DEVICE_ID&#125;&#125;,
                      &#123;&#123;PAGE_URL&#125;&#125;,
                      &#123;&#123;REFERRER_URL&#125;&#125;,
                      &#123;&#123;PAGE_TOP_LEVEL_DOMAIN&#125;&#125;,
                      &#123;&#123;APP_BUNDLE&#125;&#125;,
                      &#123;&#123;APP_NAME&#125;&#125;,
                      &#123;&#123;APP_STORE_URL&#125;&#125;,
                      &#123;&#123;USER_IP&#125;&#125;, &#123;&#123;DNT&#125;&#125;,
                      &#123;&#123;US_PRIVACY&#125;&#125;,
                      &#123;&#123;WIDTH&#125;&#125;, &#123;&#123;HEIGHT&#125;&#125;
                    </div>
                    <TextInput source="rtb_connection_info.connection_url" label="Connection URL" fullWidth />
                  </Box>
                  {isNobidOrg && (
                  <NumberInput source="rtb_connection_info.connection_timeout" label="Connection Timeout" style={{ maxWidth: 256 }} />
                  )}
                  {isNobidOrg && (
                  <NumberInput source="rtb_connection_info.max_connections" label="Max Connections" style={{ maxWidth: 256 }} />
                  )}
                  {isNobidOrg && (
                  <NumberInput source="rtb_connection_info.max_connections_per_host" label="Connections per Host" style={{ maxWidth: 256 }} />
                  )}
                  <TextInput source="openrtb_version" label="OPEN RTB Version" defaultValue="2.5" style={{ maxWidth: 256 }} />

                  <SectionHeader title="Connection Headers" />
                  <Box style={fullWidth}>
                    <ArrayInput source="rtb_connection_info.connection_headers_list" label="Connection Headers">
                      <utils.InlineFormIterator>
                        <TextInput source="key" label="Key" />
                        <TextInput source="value" label="Value" />
                      </utils.InlineFormIterator>
                    </ArrayInput>
                  </Box>

                  <SectionHeader title="Per-Region Connections" />
                  {/* minWidth 0: a grid item defaults to min-width auto, which
                      would let the table widen the column instead of scrolling
                      inside the panel. */}
                  <Box style={{ ...fullWidth, minWidth: 0 }}>
                    <RtbConnectionMapField record={formData} />
                  </Box>

                  <SectionHeader title="ID Fields" />
                  <BooleanInput source="publisher_id_required" label="Publisher ID Required" />
                  <BooleanInput source="site_id_required" label="Site ID Required" />
                  <BooleanInput source="app_id_required" label="App ID Required" />
                  <BooleanInput source="seat_id_required" label="Seat ID Required" />
                  <BooleanInput source="size_tag_required" label="Size Tag Required" />
                  <BooleanInput source="slot_id_required" label="Slot ID Required" />

                  <SectionHeader title="Bidder Flags" />
                  <BooleanInput source="append_nurl_to_admarkup" label="Append NURL to AdMarkup" />
                  <BooleanInput source="is_xandr" label="Is Xandr/Appnexus Bidder?" />
                  <BooleanInput source="is_magnite" label="Is Magnite/Rubicon Bidder?" />
                </Box>
              </FormTab>
            )
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData, ...rest }) =>
            isEdit && formData.bidder_type === 'RTB' && (
              <FormTab label="RTB" {...rest}>
                <TextInput style={{width: '40%'}}
                  multiline
                  source="rtb_config.size_tags"
                  helperText="Format: 300x25:ID\n728x90:ID"
                />
                <TextInput style={{width: '40%'}}
                  source="rtb_config.site_id" 
                  label="Site ID" />
                <TextInput style={{width: '40%'}}
                  source="rtb_config.app_id" 
                  label="App ID" />
                <TextInput style={{width: '40%'}}
                  source="rtb_config.publisher_id"
                  label="Publisher ID"
                />
                <TextInput style={{width: '40%'}}
                  source="rtb_config.tag_id" 
                  label="Tag ID" />
                <TextInput style={{width: '40%'}}
                  source="rtb_config.seat_id" 
                  label="Seat ID" />
                <BooleanInput
                  source="rtb_config.prediction_disabled"
                  label="Prediction Disabled"
                />
              </FormTab>
            )
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData, ...rest }) =>
            isEdit && formData.bidder_type === 'RTB'
            && (
              <FormTab label="Supply Chain" {...rest}>
                <h4 style={{ width: '100%'}}>Supply Chain Settings</h4>
                <BooleanInput
                  source="supplychain_enabled"
                  label="Supply Chain Enabled"
                  defaultValue={true}
                />
                <NumberInput
                  source="supplychain_maximum"
                  label="Supply Chain Maximum"
                  helperText="Maximum number of supply chain nodes allowed to send Bid Requests to this Bidder."
                  validate={[number(), minValue(1)]} step={1}
                />
                <SelectInput
                  source="supplychain_complete_value"
                  label="Supply Chain Complete Value"
                  choices={[
                    { id: 0, name: '0' },
                    { id: 1, name: '1' },
                  ]}
                  defaultValue="1"
                />

                <br />
                <TextInput
                  source="supplychain_name"
                  label="Supply chain name. Default is nobid.io"
                  style={{ width: '400px' }}
                />
                <br />
                <TextInput
                  source="supplychain_asi"
                  label="Supply chain ASI. Default is nobid.io"
                  style={{ width: '400px' }}
                />
                <br />
                <TextInput
                  source="supplychain_domain"
                  label="Supply chain domain. Default is nobid.io"
                  style={{ width: '400px' }}
                />
                <h5 style={{ width: '100%'}}>Supply Chain Publisher ID Mapping</h5>
                <ArrayInput source="supplychain_publisherid_mapping" label="" style={{ width: '50%' }}>
                    <SimpleFormIterator>
                      <ReferenceInput source="publisher_id" label="Publisher" reference="publishers"
                          basePath='publishers'
                          perPage={10}
                          sort={{ field: 'name', order: 'ASC' }}
                          filterToQuery={searchText => ({ search_name: searchText })}>
                            <AutocompleteInput label="Publishers" optionText="name" optionValue="code" style={{ width: '100%' }} />
                      </ReferenceInput>
                      
                      <TextInput source="schain_publisher_id" label="Supply Chain Publisher ID" style={{ width: '50%' }}/>
                    </SimpleFormIterator>
                </ArrayInput>
                <h5 style={{ width: '100%'}}>Restricted Supply Chain Reseller</h5>
                <>List of schain resellers that are not allowed to bid on this bidder.</>
                <ArrayInput source="restricted_schain_resellers" label="" style={{ width: '50%' }}>
                  <SimpleFormIterator>
                  <TextInput label="Supply Chain Reseller ID" style={{ width: '50%' }}/>
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            )
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData, ...rest }) =>
            isEdit && !['RTB', 'PREDICTOR'].includes(formData.bidder_type) && (
              <FormTab label="GAM Config" {...rest}>
                <Box p={5} border={1} borderRadius={5} mb={'1em'} fullWidth>
                  <BooleanInput source="adx_api_report" label="ADX Report" />
                  <TextInput
                    source="oauth_path"
                    label="Oauth path"
                    multiline={true}
                    fullWidth
                  />
                  <ArrayInput
                    source="web_property_codes"
                    label="Web property codes"
                  >
                    <SimpleFormIterator>
                      <TextInput label="Code" fullWidth />
                    </SimpleFormIterator>
                  </ArrayInput>
                </Box>
                <Box display={{ md: 'block', lg: 'flex' }} fullWidth>
                  <Box
                    flex={1}
                    mr={{ xs: 0, sm: '1em' }}
                    p={5}
                    border={1}
                    borderRadius={5}
                  >
                    <Typography variant="h6" gutterBottom>
                      {'GAM Bidder Config'}
                    </Typography>
                    <NumberInput
                      source="bidder_gam.adx_order_id"
                      label="ADX Order ID"
                    />
                    <ArrayInput
                      source="bidder_gam.adx_line_item_names"
                      label="ADX Line Items"
                    >
                      <SimpleFormIterator>
                        <TextInput label="Name" fullWidth />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </Box>
                  <Box
                    flex={1}
                    mr={{ xs: 0 }}
                    p={5}
                    border={1}
                    borderRadius={5}
                  >
                    <Typography variant="h6" gutterBottom>
                      {'GAM Platform Config'}
                    </Typography>
                    <ArrayInput source="line_item_names" label="Line Items">
                      <SimpleFormIterator>
                        <TextInput label="Name" fullWidth />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </Box>
                </Box>
              </FormTab>
            )
          }
        </FormDataConsumer>

        {isEdit && (
        <FormTab label="Reporting">
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            ['RTB', 'DIRECT'].includes(formData.bidder_type) && (
              <div>
              <ScheduledReportButton params={ {
                record: rest.record, 
                end_point: `/run_bidder_report?id=${rest.record.code}`
              } } />
              <Card style={{ width: '90%' }} raised>
                <CardHeader title="Scheduled Reports" />
                <CardContent>
                  <ArrayInput source="report_schedule_config" label="" style={{ width: '50%' }}>
                    <SimpleFormIterator>
                      <br /><span>Scheduled Report Settings</span>
                      <br /><TextInput source="recipients" label="Recipients (comma seperated)" multiline style={{ width: '400px' }} helperText="name@company.com,anothername@company.com" />
                      <br /><SelectInput label="Format" source="format" initialValue="csv"
                        choices={[
                          { id: 'xls', name: 'Excel' },
                          { id: 'csv', name: 'CSV' }
                        ]} />

                      <br />
                      <SelectInput label="Time Period" source="days"
                        initialValue="1"
                        helperText="Select the time period you want the report to run."
                        choices={[
                          { id: '0', name: 'Today' },
                          { id: 1, name: 'Yesterday' },
                          { id: 2, name: 'Last 2 Days' },
                          { id: 3, name: 'Last 3 Days' },
                          { id: 5, name: 'Last 5 Days' },
                          { id: 7, name: 'Last 7 Days' },
                          { id: 8, name: 'Last 8 Days' },
                          { id: 10, name: 'Last 10 Days' },
                          { id: 15, name: 'Last 15 Days' },
                          { id: 30, name: 'Last 30 Days' },
                          { id: 'lm', name: 'Last Month' },
                          { id: 'mtd', name: 'Month to Date' },
                          { id: 'mty', name: 'Month to Yesterday' }
                        ]} />

                      <br />
                      <SelectInput label="Time zone" source="timezone"
                        initialValue="US/Eastern"
                        helperText="Select the time zone you want the report to run."
                        choices={[
                          { id: 'US/Eastern', name: 'US/Eastern' },
                          { id: 'UTC', name: 'UTC' },
                          { id: 'US/Central', name: 'US/Central' },
                          { id: 'US/Mountain', name: 'US/Mountain' },
                          { id: 'US/Pacific', name: 'US/Pacific' },
                        ]} />
                    </SimpleFormIterator>
                  </ArrayInput>
                </CardContent>
              </Card>
              </div>
            )
          }
        </FormDataConsumer>
        </FormTab>
        )}

        {isEdit && isNobidOrg && (
        <FormTab label="Monitoring">
					<BooleanInput source="monitoring.enabled" label="Monitoring Enabled" />
					<NumberInput source="monitoring.min_threshold" label="Minimum Hourly Revenue Percentage Threshold" defaultValue={90}
					helperText="Hourly revenue should NOT drop below this percentage value."
          style={{width: '20%'}}/>
					<br/>
          <NumberInput source="monitoring.min_threshold_daily" label="Minimum Daily Revenue Percentage Threshold" defaultValue={90}
					helperText="Daily revenue should NOT drop below this percentage value."
          style={{width: '20%'}}/>
					<br/>
					{/* <NumberInput source="monitoring.max_threshold" label="Maximum Percentage Threshold" defaultValue={100}
					helperText="Hourly or Daily revenue should NOT spike above this percentage value."
          style={{width: '20%'}}/> */}
					<br/>
					<TextInput source="monitoring.email" label="Destination Notification Emails (comma separated)" 
					style={{width: '30%'}}/>
			  </FormTab>
        )}

        <FormTab label="Bidder Info">
          <h4>Bidder Info</h4>
          <TextInput
            multiline
            source="bidder_info.notes"
            label="Notes"
            helperText="Add notes about this Bidder such as contact information, traffic stats, etc."
            style={{ width: '40%' }}
          />
          
          <h4>Change Log</h4>
          <TextField readonly
            multiline
            source="bidder_info.log"
            label=" "
            style={{ width: '40%' }}
            component="pre"
          />
        </FormTab>

        {isEdit && (
        <FormTab label="Auction Optimizer">
          <SelectInput source="auction_optimizer.test_quota"
            choices={[
                { id: 0, name: '0% - No Test Quota / Always Block' },
                { id: 1, name: '1% - Allow 1% for testing' },
                { id: 2, name: '2% - Allow 2% for testing' },
                { id: 3, name: '3% - Allow 3% for testing' },
                { id: 4, name: '4% - Allow 4% for testing' },
                { id: 5, name: '5% - Allow 5% for testing' },
                { id: 10, name: '10%' },
                { id: 15, name: '15%' },
                { id: 20, name: '20%' },
                { id: 25, name: '25%' },
                { id: 30, name: '30%' },
                { id: 35, name: '35%' },
                { id: 40, name: '40%' },
                { id: 45, name: '45%' },
                { id: 50, name: '50%' },
                { id: 55, name: '55%' },
                { id: 60, name: '60%' },
                { id: 65, name: '65%' },
                { id: 70, name: '70%' },
                { id: 75, name: '75%' },
                { id: 80, name: '80%' },
                { id: 85, name: '85%' },
                { id: 90, name: '90%' },
                { id: 95, name: '95%' },
                { id: 99, name: '99%' },
                { id: 100, name: '100% - Always Allow' },
            ]}
            label="Test Quota" 
            helperText="Quota of traffic for which we do NOT apply the optimization."
            defaultValue={5}
            style={{width: '20%'}}/>
          <hr style={{width: '100%'}} />
          <NumberInput source="auction_optimizer.response_rate_minimum" label="Minimum response rate."
					  helperText="Response rate = bid responses / bid requests. Defaults to 0."
            style={{width: '20%'}}/>
          <NumberInput source="auction_optimizer.fill_rate_minimum" label="Minimum fill rate"
					  helperText="Fill rate = impressions / bid requests. Defaults to 0."
            style={{width: '20%'}}/>
          <NumberInput source="auction_optimizer.net_profit_minimum" label="Minimum net profit."
					  helperText="net profit = gross revenue - publisher revenue - cost. Defaults to 0."
            style={{width: '20%'}}/>
          <NumberInput source="auction_optimizer.rpm_minimum" label="Minimum revenue per million bid requests."
					  helperText="revenue per million bid requests = gross revenue * 1000000 / bid requests. Defaults to 0."
            style={{width: '20%'}}/>
          <NumberInput source="auction_optimizer.rpm_responses_minimum" label="Minimum revenue per million bid responses."
					  helperText="revenue per million bid responses = gross revenue * 1000000 / bid responses. Defaults to 0."
            style={{width: '20%'}}/>
          <hr style={{width: '100%'}} />
          <NumberInput source="auction_optimizer.bidder_revshare" label="Bidder Revshare. 10, 15 , 20..."
					  helperText="What is the Bidder revshare?"
            style={{width: '15%'}}/>
          <NumberInput source="auction_optimizer.bidder_profit_per_million_bid_requests_minimum" label="Bidder minimum bidder profit per million bid requests."
					  helperText="bidder profit per million bid requests = (bidder revshare/100) * gross revenue * 1000000 / bid requests. Defaults to 0."
            style={{width: '30%'}}/>
          <NumberInput source="auction_optimizer.bidder_profit_per_million_bid_responses_minimum" label="Bidder minimum bidder profit per million bid responses."
					  helperText="bidder profit per million bid requests = (bidder revshare/100) * gross revenue * 1000000 / bid responses. Defaults to 0."
            style={{width: '30%'}}/>
        </FormTab>
        )}

        {isEdit && (
        <FormTab label="Spend Summary">
							<NumberField source="revenue_stats.total_impressions" label="Week To Date Impressions" />
							<NumberField source="revenue_stats.total_revenue" label="Week To Date Spend ($)" options={{ style: 'currency', currency: 'USD' }} />
							<NumberField source="revenue_stats.total_bid_requests" label="Week To Date Bid Requests" />
              <NumberField source="revenue_stats.total_bid_responses" label="Week To Date Bid Responses" />
              <NumberField source="revenue_stats.total_response_rate" label="Response Rate" />
              <NumberField source="revenue_stats.total_avg_cpm" label="CPM" />

							<ArrayField source="revenue_stats.daily">
									<Datagrid>
											<CustomDateField label="Date"/>
											<NumberField source="impressions" label="Impressions" />
											<NumberField source="revenue" label="Spend" options={{ style: 'currency', currency: 'USD' }} />
											<NumberField source="avg_cpm" label="CPM" options={{ style: 'currency', currency: 'USD' }} />
											<NumberField source="bid_requests" label="Bid Requests" />
                      <NumberField source="bid_responses" label="Bid Responses" />
									</Datagrid>
							</ArrayField>
					</FormTab>

        )}

        {isEdit && (
        <FormTab label="Traffic">
          <FormDataConsumer>
            {({ formData }) => formData && formData.code ? (
              <TrafficPanel entityType="bidder" entityId={formData.code} trafficTypes={['bidrequest', 'bidresponse']} />
            ) : null}
          </FormDataConsumer>
        </FormTab>
        )}

      </TabbedForm>
  );
};

export const BidderEdit = ({ permissions, ...props }) => (
  <Edit title={<BidderTitle />} mutationMode='pessimistic' {...props}>
    <BidderFormWithMaterializedRecord formType="edit" permissions={permissions} {...props} />
  </Edit>
);

export const BidderCreate = ({ permissions, ...props }) => (
  <Create title="Create Bidder" mutationMode='pessimistic' transform={transformCreate} {...props}>
    <BidderForm formType="create" permissions={permissions} {...props} />
  </Create>
);

const CustomToolbar = ({permissions, ...props }) => {
  const [dialogInputValue, setDialogInputValue] = React.useState('');
  const [showDialog, setShowDialog] = useState(false);
  const handleSaveClick = async () => {
    setShowDialog(true);
  };
  const handleClose = () => {
    setShowDialog(false);
  };
  const handleSaveWithDialogInput = () => {
    setDialogInputValue('');
    props.handleSubmit(props.record);
  };
  const transformData = (data) => {
    utils.applyAdstxtRegex(data);
    utils.applyConnectionHeadersMap(data);
    utils.prependBidderLog(data, 'update', data.dialogInput);
    data.dialogInput = '';
    return data;
  };
  // On create there is no record to annotate and no "reason for update" prompt;
  // the change log is seeded via the <Create transform> instead.
  if (props.formType === 'create') {
    return (
      <Toolbar {...props}>
        <SaveButton {...props} permissions={permissions} submitOnEnter={false} />
      </Toolbar>
    );
  }
  return (
  <Toolbar {...props} {...permissions}>
    <SaveButton {...props} permissions={permissions} transform={transformData} handleSubmitWithRedirect={handleSaveClick} submitOnEnter={false}/>
    <br />
    <DateField
      source="updated_at"
      label="Update date"
      style={{ padding: '10px' }}
      showTime
    />
    <TextField source="updated_name" label="" style={{ padding: '10px' }} />
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={showDialog}
        onClose={handleClose}
      >
        <DialogTitle>What's the reason for the update?</DialogTitle>
        <DialogContent>
          <TextInput
            multiline
            autoFocus
            margin="dense"
            source="dialogInput" // This source is for the TextInput component, not the form data
            label="Reason for the update?"
            value={dialogInputValue}
            onChange={(e) => setDialogInputValue(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSaveWithDialogInput} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  </Toolbar>
  );
}
